import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ChatDockContext = createContext(null);

// Docked-panel width bounds.
// - MIN_WIDTH: historical fixed width — the point below which KC's rich
//   responses (multi-line analysis, tool-call output, tables) start to clip.
// - MAX_WIDTH: ceiling beyond which the panel dominates the screen on a
//   typical laptop viewport. Viewport-relative cap (50% innerWidth) overrides
//   this on small screens — see clampWidth below.
const MIN_WIDTH = 360;
const MAX_WIDTH = 700;
const STORAGE_KEY = 'kc_docked_width';

// Clamp a requested width to [MIN_WIDTH, min(MAX_WIDTH, 50% of viewport)].
// The viewport cap stops the panel taking over small screens (req 8).
function clampWidth(raw) {
  const viewportCap = typeof window !== 'undefined'
    ? Math.max(MIN_WIDTH, Math.floor(window.innerWidth * 0.5))
    : MAX_WIDTH;
  const upper = Math.min(MAX_WIDTH, viewportCap);
  return Math.max(MIN_WIDTH, Math.min(upper, raw));
}

export function ChatDockProvider({ children }) {
  const [isDocked, setIsDocked] = useState(() => {
    return localStorage.getItem('nvst_chat_docked') === 'true';
  });

  const [dockedWidth, setDockedWidthState] = useState(() => {
    const raw = parseInt(localStorage.getItem(STORAGE_KEY), 10);
    return clampWidth(Number.isFinite(raw) ? raw : MIN_WIDTH);
  });

  const toggleDock = useCallback(() => {
    setIsDocked(prev => {
      const next = !prev;
      localStorage.setItem('nvst_chat_docked', String(next));
      return next;
    });
  }, []);

  const undock = useCallback(() => {
    setIsDocked(false);
    localStorage.setItem('nvst_chat_docked', 'false');
  }, []);

  // Public setter — clamp, persist, commit React state. Called on mouseup
  // (end of drag) and on double-click-to-reset. During an active drag, the
  // panel/layout follow the CSS custom property directly for framerate-smooth
  // updates without per-frame React re-renders.
  const setDockedWidth = useCallback((w) => {
    const clamped = clampWidth(w);
    setDockedWidthState(clamped);
    localStorage.setItem(STORAGE_KEY, String(clamped));
  }, []);

  // Viewport-cap enforcement (req 8): if the user shrinks the browser window
  // such that the stored width would exceed 50% of viewport, shrink it.
  useEffect(() => {
    const onResize = () => {
      setDockedWidthState(prev => {
        const cap = Math.floor(window.innerWidth * 0.5);
        if (prev > cap) {
          const next = Math.max(MIN_WIDTH, cap);
          // Persist so the cap survives a reload at the same small viewport.
          localStorage.setItem(STORAGE_KEY, String(next));
          return next;
        }
        return prev;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Sync the CSS custom property whenever the committed React state changes.
  // This is the authoritative value at rest; during an active drag, the
  // handler writes to the same property directly and commits via
  // setDockedWidth on mouseup (which lands here as a no-op CSS write but
  // triggers the Layout re-render so any non-var sx paths catch up).
  useEffect(() => {
    document.documentElement.style.setProperty('--kc-docked-width', `${dockedWidth}px`);
  }, [dockedWidth]);

  return (
    <ChatDockContext.Provider
      value={{
        isDocked,
        toggleDock,
        undock,
        dockedWidth,
        setDockedWidth,
        MIN_WIDTH,
        MAX_WIDTH
      }}
    >
      {children}
    </ChatDockContext.Provider>
  );
}

export function useChatDock() {
  const context = useContext(ChatDockContext);
  if (!context) {
    // Safe defaults when used outside the provider (e.g. the login page, which
    // renders without Layout). Width values here are only returned for calling
    // code that destructures them; they won't drive any rendering since
    // isDocked is false.
    return {
      isDocked: false,
      toggleDock: () => {},
      undock: () => {},
      dockedWidth: MIN_WIDTH,
      setDockedWidth: () => {},
      MIN_WIDTH,
      MAX_WIDTH
    };
  }
  return context;
}

export default ChatDockContext;
